import { yupResolver } from '@hookform/resolvers/yup';
import {
  AddCircleOutline,
  CheckCircleOutlined,
  FiberManualRecord,
  HelpOutline,
  RemoveCircleOutline,
  SaveOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
  ChangeEvent, FC, memo, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { MAX_CUSTOM_INPUT_FIELDS, trimTrailingEmptySlots } from 'src/constants/customInputFields';
import { appBarHeight } from 'src/constants/layout';
import LicenseContext from 'src/contexts/LicenseContext';
import {
  CompanyOrderTimeRange,
  CompanyOrderTimeRangeKinds,
  DeliveryListGroupingValues,
  LicenseEntity,
  LoadingUnloadingDuration,
} from 'src/entities/licenseEntity';
import { COMPANY_QUERY_KEY, useQueryCompany } from 'src/hooks/useQueryCompany';
import { datetimeUtil } from 'src/utils/datetime.util';
import * as yup from 'yup';

import arrayUtil from '../../utils/array.util';

import { Field, SectionCard, ToggleRow } from './SectionCard';
import { SECTION_DEFS } from './sections';
import { SettingsSideNav } from './SettingsSideNav';
import { VehicleAttributeSection } from './VehicleAttributeSection';

/** 未保存のまま放置されないよう保存ボタンを脈打たせる。色はテーマの primary (#2C87C9) */
const savePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(44, 135, 201, 0.45); }
  70% { box-shadow: 0 0 0 8px rgba(44, 135, 201, 0); }
  100% { box-shadow: 0 0 0 0 rgba(44, 135, 201, 0); }
`;

const denseInputProps = { inputProps: { style: { fontSize: 13 } } };

type CustomInputFieldFilter = 'all' | 'used' | 'unused';

const CUSTOM_INPUT_FIELD_FILTERS: { key: CustomInputFieldFilter; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'used', label: '使用中' },
  { key: 'unused', label: '未使用' },
];

type ThresholdName =
  | 'big_weight_truck_threshold'
  | 'big_volume_truck_threshold'
  | 'big_weight_order_threshold'
  | 'big_volume_order_threshold'
  | 'small_weight_order_threshold'
  | 'small_volume_order_threshold';

/**
 * 大型車両の割付条件を行 × 列で持つ。
 * デザインには数量列もあるが配車アルゴリズムが数量軸に未対応のため今回は重量・体積だけにし、
 * 列を足すだけで数量に対応できる形にしてある。
 */
const BIG_TRUCK_ROWS: {
  title: string;
  suffix?: string;
  hint?: string;
  weight: ThresholdName;
  volume: ThresholdName;
  highlight: boolean;
}[] = [
  {
    title: '大型車両の定義',
    suffix: '（入力値以上の車両を大型車両として扱います。）',
    weight: 'big_weight_truck_threshold',
    volume: 'big_volume_truck_threshold',
    highlight: true,
  },
  {
    title: '優先的に割り付ける（以上）',
    hint: '条件を満たす荷物を大型車両へ優先して割り当てます。',
    weight: 'big_weight_order_threshold',
    volume: 'big_volume_order_threshold',
    highlight: false,
  },
  {
    title: '割り付けない（以下）',
    hint: '小口の荷物を大型車両から除外します。',
    weight: 'small_weight_order_threshold',
    volume: 'small_volume_order_threshold',
    highlight: false,
  },
];

type CostName =
  | 'driver_cost_yen_per_hours'
  | 'truck_fuel_cost_yen_per_km'
  | 'truck_insurance_fee_yen_per_day'
  | 'truck_repair_cost_yen_per_day'
  | 'truck_expressway_fee_yen_per_shift';

const COST_FIELDS: { label: string; name: CostName }[] = [
  { label: '時給', name: 'driver_cost_yen_per_hours' },
  { label: '1kmあたりの燃料費', name: 'truck_fuel_cost_yen_per_km' },
  { label: '1日あたりの保険料', name: 'truck_insurance_fee_yen_per_day' },
  { label: '1日あたりの車両修繕費', name: 'truck_repair_cost_yen_per_day' },
  { label: '配送計画あたりの高速代', name: 'truck_expressway_fee_yen_per_shift' },
];

const bigTruckCombination = (_value: number, context: yup.TestContext) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {
    big_weight_truck_threshold, big_volume_truck_threshold,
    big_weight_order_threshold, big_volume_order_threshold,
    small_weight_order_threshold, small_volume_order_threshold
  } = context.parent;
  if ((big_weight_order_threshold || big_volume_order_threshold
    || small_weight_order_threshold || small_volume_order_threshold)
    && (!big_weight_truck_threshold && !big_volume_truck_threshold)) {
      return false;
  }
  return true;
};

const companyOrderTimeRange = yup.object({
  start_at: yup
    .string()
    .nullable()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, '時間はHH:mm形式で入力してください')
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value) => (value === '' ? null : value)),
  end_at: yup
    .string()
    .nullable()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, '時間はHH:mm形式で入力してください')
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value) => (value === '' ? null : value)),
}).test('both-or-none', '開始時間・終了時間の両方を入力するか、両方を空にしてください', (value) => {
  const { start_at: startAt, end_at: endAt } = value;
  return (!!startAt && !!endAt) || (!startAt && !endAt);
});

const positiveInteger = () => yup
  .number()
  .integer('整数で入力してください')
  .positive('0以下は入力できません')
  .typeError('半角数で入力してください')
  .nullable()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value));

const zeroOrPositiveInteger = () => yup
  .number()
  .integer('整数で入力してください')
  .min(0, '0未満は入力できません')
  .typeError('半角数で入力してください')
  .nullable()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value));

const bigTruckThreshold = () => positiveInteger()
  .test(
    'combination_validation',
    '詳細条件を設定した場合は、大型車両の条件を入力してください。',
    bigTruckCombination,
  );

const schema = yup.object({
  orders_limit_per_driver: positiveInteger(),
  shuttle_limit: positiveInteger(),
  unloading_position_limit_by_a_shuttle: positiveInteger(),
  driver_cost_yen_per_hours: zeroOrPositiveInteger(),
  truck_expressway_fee_yen_per_shift: zeroOrPositiveInteger(),
  truck_fuel_cost_yen_per_km: zeroOrPositiveInteger(),
  truck_insurance_fee_yen_per_day: zeroOrPositiveInteger(),
  truck_repair_cost_yen_per_day: zeroOrPositiveInteger(),
  big_weight_truck_threshold: bigTruckThreshold(),
  big_volume_truck_threshold: bigTruckThreshold(),
  big_weight_order_threshold: positiveInteger(),
  big_volume_order_threshold: positiveInteger(),
  small_weight_order_threshold: positiveInteger(),
  small_volume_order_threshold: positiveInteger(),
  company_order_time_ranges_attributes: yup.array().of(companyOrderTimeRange),
});

const EngineConfigPresenter: FC = memo(() => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [companyId, setCompanyId] = useState(0);
  const { data: companyData } = useQueryCompany(companyId);

  const [activeSectionId, setActiveSectionId] = useState<string>('global');
  const [dirty, setDirty] = useState<boolean>(false);
  const markDirty = useCallback(() => setDirty(true), []);

  /**
   * フォームの中の入力が変わったときだけ未保存にする。
   *
   * Why not markDirty を直接渡す: 車両表示設定 (#329) のダイアログは portal で DOM 上は
   * body 直下だが、React ツリー上はこのフォームの子なので change がここまで上がってくる。
   * 別 API で即時保存しているのに会社設定が未保存扱いになってしまう。
   */
  const markDirtyFromForm = useCallback((event: ChangeEvent<HTMLFormElement>) => {
    if (event.currentTarget.contains(event.target)) markDirty();
  }, [markDirty]);

  const [orderTimeRangeAM, setOrderTimeRangeAM] = useState<CompanyOrderTimeRange>();
  const [orderTimeRangePM, setOrderTimeRangePM] = useState<CompanyOrderTimeRange>();
  const [orderTimeRangeALL, setOrderTimeRangeALL] = useState<CompanyOrderTimeRange>();

  // AM → PM → ALL の順。この添字が company_order_time_ranges_attributes の添字になり
  // errors の添字と対応する
  const orderTimeRanges = [
    orderTimeRangeAM, orderTimeRangePM, orderTimeRangeALL
  ].filter((it) => it);

  useEffect(() => {
    if (!companyData) return;

    setOrderTimeRangeAM(
      companyData.company_order_time_ranges_attributes?.find((it) => it.kind === 'AM') || {
        id: null, kind: 'AM', start_at: null, end_at: null
      }
    );
    setOrderTimeRangePM(
      companyData.company_order_time_ranges_attributes?.find((it) => it.kind === 'PM') || {
        id: null, kind: 'PM', start_at: null, end_at: null
      }
    );
    setOrderTimeRangeALL(
      companyData.company_order_time_ranges_attributes?.find((it) => it.kind === 'ALL') || {
        id: null, kind: 'ALL', start_at: null, end_at: null
      }
    );
  }, [companyData]);

  // スロットは保存済みの件数から始めて「項目を追加」で上限まで伸ばす。
  // Why not: 常に上限件数を並べる形にしないのは、空欄スロットを保存時に落とす
  // （消費側が空名の列を作らないようにする）ため、未使用スロットが永続しないから。
  const [customInputFields, setCustomInputFields] = useState<string[]>([]);
  const [customInputFieldFilter, setCustomInputFieldFilter] = useState<CustomInputFieldFilter>('all');
  const filledCustomInputFields = useMemo(
    () => customInputFields.filter((maybe) => maybe),
    [customInputFields]
  );
  const visibleCustomInputFields = useMemo(
    () => customInputFields
      .map((value, index) => ({ value, index }))
      .filter(({ value }) => {
        if (customInputFieldFilter === 'used') return !!value;
        if (customInputFieldFilter === 'unused') return !value;
        return true;
      }),
    [customInputFields, customInputFieldFilter]
  );

  const updateCustomInputField = (index: number, value: string) => {
    if (filledCustomInputFields.includes(value)) {
      enqueueSnackbar('既に存在する独自入力項目です', { variant: 'warning' });
      return;
    }

    setCustomInputFields((prev) => prev.map((it, i) => (i === index ? value : it)));
  };

  const addCustomInputField = () => {
    setCustomInputFields((prev) => (prev.length >= MAX_CUSTOM_INPUT_FIELDS ? prev : [...prev, '']));
    markDirty();
  };

  useEffect(() => {
    if (!companyData) return;

    // custom_input_fields は空欄除外済みなので、スロットは生の配列を読む
    setCustomInputFields((companyData.custom_input_field_slots ?? []).slice(0, MAX_CUSTOM_INPUT_FIELDS));
  }, [companyData]);

  const {
    reset,
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<LicenseEntity>({
    resolver: yupResolver(schema),
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [loadingDefaultMinutes, setLoadingDefaultMinutes] = useState<number>(0);
  const [unloadingDefaultMinutes, setUnloadingDefaultMinutes] = useState<number>(0);
  const [loadingUnloadingDurations, setLoadingUnloadingDurations] = useState<LoadingUnloadingDuration[]>([]);
  const [loadingUnloadingDurationErrors, setLoadingUnloadingDurationErrors] = useState<{ idx: number; key: string, message: string }[]>([]);
  const [deliverylListGrouping, setDeliveryListGrouping] = useState<DeliveryListGroupingValues>('groupByAddress');
  const [travelTimeScale, setTravelTimeScale] = useState<number>(0);

  const buttonOnClick: SubmitHandler<LicenseEntity> = async (data: LicenseEntity) => {
    const companyRequestPath = '/api/v2/company';

    setIsLoading(true);

    const loadingDefaultSeconds = loadingDefaultMinutes > 0 ? datetimeUtil.minutesToSeconds(loadingDefaultMinutes) : null;
    const unloadingDefaultSeconds = unloadingDefaultMinutes > 0 ? datetimeUtil.minutesToSeconds(unloadingDefaultMinutes) : null;

    const companyOrderTimeRangesAttributes = data.company_order_time_ranges_attributes.map((it) => {
      if (!!it.start_at && !!it.end_at) return it;
      if (it.id) {
        return {
          ...it,
          _destroy: true,
        };
      }
      return null;
    }).filter((it) => it);

    // 「荷姿ごとの変換ルール」「休憩時間設定 (幹線輸送)」は画面から外したのでキー自体を送らない。
    // キーが無ければ API は既存レコードに触らない。
    //
    // Why not: 読み込んだ値をそのまま送り返す形にしなかった理由が 2 つある。
    //   - packing_style_and_weight_volumes は API がキーの有無で「全削除して作り直す」ため、
    //     serialize が未登録の荷姿まで weight/volume を null で返してくる分まで登録されてしまう
    //   - company_trunk_transportation_setting_attributes は permit されないキーだけを送ると
    //     空のレコードが新規作成される（companies_controller_spec で確認済み）
    const {
      packing_style_and_weight_volumes: _packingStyleAndWeightVolumes,
      company_trunk_transportation_setting_attributes: _companyTrunkTransportationSetting,
      ...companyParams
    } = data;

    const requestEntity = {
      ...companyParams,
      delivery_list_grouping: deliverylListGrouping,
      custom_input_fields: trimTrailingEmptySlots(customInputFields),
      travel_time_scale: ((travelTimeScale * -1) + 100.0) / 100,
      company_order_time_ranges_attributes: companyOrderTimeRangesAttributes,
      loading_duration_seconds: loadingDefaultSeconds,
      unloading_duration_seconds: unloadingDefaultSeconds,
      loading_unloading_duration_table: loadingUnloadingDurations.map((it) => ({
        ...it, volumeM3: it.volumeM3 ? it.volumeM3 : null, loadingDurationSeconds: it.loadingDurationSeconds * 60, unloadingDurationSeconds: it.unloadingDurationSeconds * 60,
      }))
    };

    try {
      await axios.post(companyRequestPath, requestEntity);
      void queryClient.invalidateQueries({
        queryKey: [COMPANY_QUERY_KEY, { companyId }]
      });
      void queryClient.invalidateQueries({
        queryKey: ['useSessionRequest']
      });
      setDirty(false);
      enqueueSnackbar('設定を保存しました', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('エラーが発生しました');
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!companyData) return;

    const { ...newCompanyData } = companyData;

    reset({
      ...newCompanyData,
      custom_input_fields: []
    });
  }, [companyData, reset]);

  useEffect(() => {
    if (!licenseContext.config) return;

    setCompanyId(licenseContext.config.selected_company_id);
  }, [licenseContext.config]);

  const updateLoadingDefaultMinutes = (value: number) => {
    if (Number.isNaN(value)) return;
    if (value < 0) return;

    setLoadingDefaultMinutes(value);
  };

  const updateUnloadingDefaultMinutes = (value: number) => {
    if (Number.isNaN(value)) return;
    if (value < 0) return;

    setUnloadingDefaultMinutes(value);
  };

  const loadingUnloadingDurationDefault: LoadingUnloadingDuration = {
    weightKg: arrayUtil.max(loadingUnloadingDurations.map((it) => it.weightKg)) + 1,
    volumeM3: arrayUtil.max(loadingUnloadingDurations.map((it) => it.volumeM3)) + 1,
    loadingDurationSeconds: loadingDefaultMinutes || Math.ceil(arrayUtil.max(loadingUnloadingDurations.map((it) => it.loadingDurationSeconds))),
    unloadingDurationSeconds: unloadingDefaultMinutes || Math.ceil(arrayUtil.max(loadingUnloadingDurations.map((it) => it.unloadingDurationSeconds))),
  };

  const initializeLoadingUnloadingDuration = () => {
    setLoadingUnloadingDurations([loadingUnloadingDurationDefault]);
    markDirty();
  };

  const addLoadingUnloadingDuration = (index: number) => {
    const newLoadingUnloadingDurations = loadingUnloadingDurations.map((loadingUnloadingDuration, i) => {
      if (i !== (index)) return loadingUnloadingDuration;
      return [
        loadingUnloadingDuration,
        loadingUnloadingDurationDefault
      ];
    });
    setLoadingUnloadingDurations(newLoadingUnloadingDurations.flat());
    markDirty();
  };

  const removeLoadingUnloadingDuration = (index: number) => {
    const newLoadingUnloadingDurations = loadingUnloadingDurations.filter((_, i) => i !== index);
    setLoadingUnloadingDurations(newLoadingUnloadingDurations);
    markDirty();
  };

  const updateLoadingUnloadingDuration = (index: number, key: keyof LoadingUnloadingDuration, value: string) => {
    const invalidValue = value !== '' && Number.isNaN(value);
    if (invalidValue) return;

    const newLoadingUnloadingDurations = loadingUnloadingDurations.map((loadingUnloadingDuration, i) => {
      if (i !== index) return loadingUnloadingDuration;
      return {
        ...loadingUnloadingDuration,
        [key]: value
      };
    });

    setLoadingUnloadingDurations(newLoadingUnloadingDurations);
  };

  useEffect(() => {
    if (!companyData) return;

    if (companyData.loading_duration_seconds) {
      setLoadingDefaultMinutes(companyData.loading_duration_seconds / 60);
    }

    if (companyData.unloading_duration_seconds) {
      setUnloadingDefaultMinutes(companyData.unloading_duration_seconds / 60);
    }

    if (companyData.loading_unloading_duration_table) {
      setLoadingUnloadingDurations(companyData.loading_unloading_duration_table.map((it) => ({
        ...it, loadingDurationSeconds: it.loadingDurationSeconds / 60, unloadingDurationSeconds: it.unloadingDurationSeconds / 60,
      })));
    } else {
      setLoadingUnloadingDurations([]);
    }

    if (companyData.delivery_list_grouping) {
      setDeliveryListGrouping(companyData.delivery_list_grouping);
    }
  }, [companyData]);

  useEffect(() => {
    const validateWeightKgPresent = (current: LoadingUnloadingDuration) => {
      if (!current.weightKg) {
        return '重量を入力してください';
      }
      return '';
    };

    const validateWeightKgGtPrev = (prev: LoadingUnloadingDuration | undefined, current: LoadingUnloadingDuration) => {
      if (prev?.weightKg && current.weightKg && Number(prev?.weightKg) > Number(current.weightKg)) {
        return '重量は小さい順に入力してください';
      }
      return '';
    };

    const validateVolumeM3GtPrev = (prev: LoadingUnloadingDuration | undefined, current: LoadingUnloadingDuration) => {
      if (!prev?.volumeM3) return '';

      if (Number(prev.volumeM3) > Number(current.volumeM3)) {
        return '立米は小さい順に入力してください';
      }
      return '';
    };

    const validateLoadingSecondsPresent = (current: LoadingUnloadingDuration) => {
      if (!current.loadingDurationSeconds) {
        return '積込時間を入力してください';
      }
      return '';
    };

    const validateUnloadingSecondsPresent = (current: LoadingUnloadingDuration) => {
      if (!current.unloadingDurationSeconds) {
        return '降地時間を入力してください';
      }
      return '';
    };

    const errs = loadingUnloadingDurations
      .flatMap((loadingUnloadingDuration, index) => {
        const prev = loadingUnloadingDurations[index - 1];

        const weightErrors = [
          validateWeightKgPresent(loadingUnloadingDuration),
          validateWeightKgGtPrev(prev, loadingUnloadingDuration)
        ].filter((maybe) => maybe);

        return [
          { idx: index, key: 'weightKg', message: weightErrors.join('、') },
          { idx: index, key: 'volumeM3', message: validateVolumeM3GtPrev(prev, loadingUnloadingDuration) },
          { idx: index, key: 'loadingDurationSeconds', message: validateLoadingSecondsPresent(loadingUnloadingDuration) },
          { idx: index, key: 'unloadingDurationSeconds', message: validateUnloadingSecondsPresent(loadingUnloadingDuration) }
        ].filter((err) => err.message !== '');
      });

    setLoadingUnloadingDurationErrors(errs);
  }, [loadingUnloadingDurations]);

  const onChangeDeliveryListGrouping = (event: ChangeEvent<HTMLInputElement>) => {
    setDeliveryListGrouping(event.target.value as DeliveryListGroupingValues);
  };

  useEffect(() => {
    if (!companyData) return;

    const val = companyData.travel_time_scale ?? 1;
    setTravelTimeScale((Math.round(val * 100.0) - 100) * -1);
  }, [companyData]);

  const handleOrderTimeRangeChange = (kind: CompanyOrderTimeRangeKinds, key: string, value: string) => {
    if (kind === 'AM') {
      setOrderTimeRangeAM((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (kind === 'PM') {
      setOrderTimeRangePM((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (kind === 'ALL') {
      setOrderTimeRangeALL((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  useEffect(() => {
    setValue('company_order_time_ranges_attributes', orderTimeRanges);
  }, [orderTimeRanges, setValue]);

  const contentHeight = useMemo(() => `calc(100vh - ${appBarHeight}px)`, []);

  const hasRuleError = loadingUnloadingDurationErrors.length > 0;

  const scrollToSection = (id: string) => {
    setActiveSectionId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 大型車両の定義と詳細条件の組合せエラーは 2 つの欄に同じ文言が出る。
  // 30 文字あってセル幅 150px では読めないので、セルは赤枠だけにしてテーブルの下に 1 回出す
  const combinationErrorMessage = [errors.big_weight_truck_threshold, errors.big_volume_truck_threshold]
    .find((error) => error?.type === 'combination_validation')?.message;

  const thresholdCell = (name: ThresholdName) => {
    const error = errors[name];
    const inCellMessage = error?.type === 'combination_validation' ? undefined : error?.message;

    return (
      <Box sx={{ flex: '0 0 150px', px: 1, py: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          type="number"
          id={name}
          {...register(name)}
          error={!!error}
          helperText={inCellMessage}
          {...denseInputProps}
        />
      </Box>
    );
  };

  return (
    <>
      <title>システム設定</title>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: contentHeight,
          minHeight: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 3,
            py: 1.75,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h1" sx={{ fontSize: 18, fontWeight: 700 }}>システム設定</Typography>
          </Box>
          {hasRuleError && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              icon={<WarningAmberOutlined sx={{ fontSize: 16 }} />}
              label="荷捌き時間設定にエラーがあります"
              sx={{ fontSize: 12, fontWeight: 700, flexShrink: 0 }}
            />
          )}
          {dirty && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberOutlined sx={{ fontSize: 16 }} />}
              label="未保存の変更があります"
              sx={{
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
                // LogTheme の warning は純黄色 (#FFD600) で白背景だと読めないので dark を使う
                color: 'warning.dark',
                borderColor: 'warning.dark',
                '& .MuiChip-icon': { color: 'warning.dark' },
              }}
            />
          )}
          <LoadingButton
            size="small"
            variant={dirty ? 'contained' : 'outlined'}
            color="primary"
            startIcon={dirty ? <SaveOutlined /> : <CheckCircleOutlined />}
            loading={isLoading}
            disabled={hasRuleError}
            onClick={() => { void handleSubmit(buttonOnClick)(); }}
            sx={{
              fontSize: 12.5,
              fontWeight: 700,
              minWidth: 96,
              flexShrink: 0,
              ...(dirty ? { animation: `${savePulse} 2s infinite` } : {}),
            }}
          >
            {dirty ? '保存する' : '保存済み'}
          </LoadingButton>
        </Stack>

        <Box sx={{
          flex: 1, minWidth: 0, minHeight: 0, display: 'flex',
        }}
        >
          <SettingsSideNav activeSectionId={activeSectionId} onSelect={scrollToSection} />

          <Box
            component="form"
            noValidate
            onChangeCapture={markDirtyFromForm}
            sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}
          >
            <Stack
              spacing={0}
              sx={{
                maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, pb: 5,
              }}
            >
              <SectionCard
                id="global"
                title="グローバル設定"
                description="自動配車全体に適用される基本ルールです。"
                icon={SECTION_DEFS.global.icon}
              >
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Field label="最大回転数">
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      id="shuttle_limit"
                      {...register('shuttle_limit')}
                      error={'shuttle_limit' in errors}
                      helperText={errors.shuttle_limit?.message}
                      {...denseInputProps}
                    />
                  </Field>
                  <Field label="1回転あたりの最大納品先件数" width={240}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      placeholder="制限なし"
                      id="unloading_position_limit_by_a_shuttle"
                      {...register('unloading_position_limit_by_a_shuttle')}
                      error={'unloading_position_limit_by_a_shuttle' in errors}
                      helperText={errors.unloading_position_limit_by_a_shuttle?.message}
                      {...denseInputProps}
                    />
                  </Field>
                </Stack>
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  <Controller
                    control={control}
                    name="activate_constraint_load_after_not_cleared"
                    render={({ field }) => (
                      <ToggleRow
                        label="空荷時のみ積込を許可する"
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="load_after_initial_stock"
                    render={({ field }) => (
                      <ToggleRow
                        label="宵積み時に納品前の積込を許可する"
                        hint="宵積み時、既存の荷物を降ろす前でもトラックに空きがあれば荷物を積み込むことができます。"
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="set_current_time_to_algorithm"
                    render={({ field }) => (
                      <ToggleRow
                        label="現在時刻より過去の配車計画は変更しない"
                        hint="OFFにすると、現在時刻よりも過去の計画も変更されます。"
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="load_start_at_main_warehouse"
                    render={({ field }) => (
                      <ToggleRow
                        label="主要倉庫から積み込みを開始する"
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Box>
              </SectionCard>

              <SectionCard
                id="speed"
                title="移動速度の調整"
                description="ルート計算に用いる標準速度を調整します。"
                icon={SECTION_DEFS.speed.icon}
                action={(
                  <Chip
                    size="small"
                    label={travelTimeScale === 0
                      ? '標準'
                      : `${Math.abs(travelTimeScale)}% ${travelTimeScale > 0 ? '速くする' : '遅くする'}`}
                    color={travelTimeScale === 0 ? 'default' : 'primary'}
                    sx={{ fontSize: 11.5, fontWeight: 700, flexShrink: 0 }}
                  />
                )}
              >
                <Box sx={{ px: 1, maxWidth: 520 }}>
                  <Slider
                    value={travelTimeScale}
                    onChange={(_, value) => {
                      setTravelTimeScale(value);
                      markDirty();
                    }}
                    min={-50}
                    max={50}
                    step={1}
                    marks={[
                      { value: -50, label: '-50%' },
                      { value: 0, label: '標準' },
                      { value: 50, label: '+50%' },
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value > 0 ? '+' : ''}${value}%`}
                    aria-label="移動速度の調整"
                    sx={{ '& .MuiSlider-markLabel': { fontSize: 11 } }}
                  />
                </Box>
              </SectionCard>

              <SectionCard
                id="timeband"
                title="時間帯の設定"
                description="AM / PM / ALL 指定の案件をどの時間帯として扱うか設定します。"
                icon={SECTION_DEFS.timeband.icon}
              >
                <Stack spacing={1}>
                  {orderTimeRanges.map((it, index) => (
                    <Stack
                      key={`order-time-range-${it.kind}`}
                      direction="row"
                      alignItems="flex-start"
                      spacing={1.5}
                      sx={{
                        px: 1.25, py: 1, borderRadius: 1.5, border: 1, borderColor: 'divider',
                      }}
                    >
                      <Chip
                        size="small"
                        label={it.kind}
                        variant="outlined"
                        sx={{
                          width: 56, fontSize: 11.5, fontWeight: 700, mt: 0.5, flexShrink: 0,
                        }}
                      />
                      <TextField
                        size="small"
                        type="time"
                        id={`order_time_range_start_at_${it.kind}`}
                        value={it.start_at || ''}
                        onChange={(event) => handleOrderTimeRangeChange(it.kind, 'start_at', event.target.value)}
                        error={errors.company_order_time_ranges_attributes?.[index]?.type === 'both-or-none' || !!errors.company_order_time_ranges_attributes?.[index]?.start_at}
                        helperText={errors.company_order_time_ranges_attributes?.[index]?.message || errors.company_order_time_ranges_attributes?.[index]?.start_at?.message}
                        sx={{ width: 150 }}
                        {...denseInputProps}
                      />
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1 }}>〜</Typography>
                      <TextField
                        size="small"
                        type="time"
                        id={`order_time_range_end_at_${it.kind}`}
                        value={it.end_at || ''}
                        onChange={(event) => handleOrderTimeRangeChange(it.kind, 'end_at', event.target.value)}
                        error={!!errors.company_order_time_ranges_attributes?.[index]?.end_at}
                        helperText={errors.company_order_time_ranges_attributes?.[index]?.end_at?.message}
                        sx={{ width: 150 }}
                        {...denseInputProps}
                      />
                    </Stack>
                  ))}
                </Stack>
              </SectionCard>

              <SectionCard
                id="largeVehicle"
                title="大型車両と荷物の割り付け条件設定"
                description="大型車両とみなす基準と、割り付ける荷物の条件を設定します。"
                icon={SECTION_DEFS.largeVehicle.icon}
              >
                <Box sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
                }}
                >
                  <Stack direction="row" sx={{ bgcolor: 'action.hover' }}>
                    <Box sx={{ flex: '1 1 40%' }} />
                    {['重量（kg）', '体積（m³）'].map((header) => (
                      <Box key={header} sx={{ flex: '0 0 150px', px: 1, py: 0.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                          {header}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  {BIG_TRUCK_ROWS.map((row, index) => (
                    <Stack
                      key={row.title}
                      direction="row"
                      alignItems="flex-start"
                      sx={{
                        borderTop: index === 0 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        bgcolor: row.highlight ? 'action.hover' : 'transparent',
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        flexWrap="wrap"
                        sx={{ flex: '1 1 40%', px: 1.5, py: 1.5 }}
                      >
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{row.title}</Typography>
                        {row.suffix && (
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.suffix}</Typography>
                        )}
                        {row.hint && (
                          <Tooltip title={row.hint} arrow>
                            <HelpOutline sx={{ fontSize: 14, color: 'text.disabled' }} />
                          </Tooltip>
                        )}
                      </Stack>
                      {thresholdCell(row.weight)}
                      {thresholdCell(row.volume)}
                    </Stack>
                  ))}
                </Box>
                {combinationErrorMessage && (
                  <Typography sx={{ fontSize: 11.5, color: 'error.main', mt: 1, lineHeight: 1.5 }}>
                    {combinationErrorMessage}
                  </Typography>
                )}
                <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mt: 1 }}>
                  <FiberManualRecord sx={{ fontSize: 6, mt: 0.75, color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.5 }}>
                    荷物の条件は、納品地点ごとに合算した荷物量の数字で判断します。
                  </Typography>
                </Stack>
              </SectionCard>

              <SectionCard
                id="cost"
                title="コスト設定"
                description="収支計算に使用する単価です。"
                icon={SECTION_DEFS.cost.icon}
              >
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {COST_FIELDS.map((cost) => (
                    <Field key={cost.name} label={cost.label}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        id={cost.name}
                        {...register(cost.name)}
                        error={!!errors[cost.name]}
                        helperText={errors[cost.name]?.message}
                        InputProps={{ endAdornment: <InputAdornment position="end">円</InputAdornment> }}
                        {...denseInputProps}
                      />
                    </Field>
                  ))}
                </Stack>
              </SectionCard>

              <SectionCard
                id="handling"
                title="荷捌き時間設定"
                description="積込・降地にかかる標準時間と、重量・体積ごとの個別時間を設定します。"
                icon={SECTION_DEFS.handling.icon}
                action={loadingUnloadingDurations.length === 0 ? (
                  <Button
                    size="small"
                    startIcon={<AddCircleOutline />}
                    sx={{ fontSize: 12, flexShrink: 0 }}
                    // 読み込み前に足しても、companyData 到着時の effect で上書きされて消える
                    disabled={!companyData}
                    onClick={initializeLoadingUnloadingDuration}
                  >
                    個別設定を追加
                  </Button>
                ) : undefined}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>デフォルト値設定</Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <Field label="積込時間(分)" width={160}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={loadingDefaultMinutes || ''}
                      onChange={(event) => updateLoadingDefaultMinutes(Number(event.target.value))}
                      {...denseInputProps}
                    />
                  </Field>
                  <Field label="降地時間(分)" width={160}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={unloadingDefaultMinutes || ''}
                      onChange={(event) => updateUnloadingDefaultMinutes(Number(event.target.value))}
                      {...denseInputProps}
                    />
                  </Field>
                </Stack>

                <Divider sx={{ mb: 1.5 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>個別設定（重量・体積ごと）</Typography>
                {loadingUnloadingDurations.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {/* 読み込み中に「ありません」と出すと設定が消えたように見えるので出し分ける */}
                    {companyData
                      ? '個別設定はありません。「個別設定を追加」から条件を追加できます。'
                      : '読み込み中...'}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {loadingUnloadingDurations.map((loadingUnloadingDuration, index) => (
                      <Stack
                        key={['loadingUnloadingDuration', index].join('-')}
                        direction="row"
                        alignItems="flex-start"
                        spacing={1.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{
                          p: 1.25, borderRadius: 1.5, border: 1, borderColor: 'divider',
                        }}
                      >
                        <Field label="〜重量(kg)" width={130}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={loadingUnloadingDuration.weightKg ?? ''}
                            onChange={(event) => updateLoadingUnloadingDuration(index, 'weightKg', event.target.value)}
                            error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'weightKg')}
                            helperText={loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'weightKg')?.message}
                            {...denseInputProps}
                          />
                        </Field>
                        <Field label="〜体積(m³)" width={130}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={loadingUnloadingDuration.volumeM3 ?? ''}
                            onChange={(event) => updateLoadingUnloadingDuration(index, 'volumeM3', event.target.value)}
                            error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'volumeM3')}
                            helperText={loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'volumeM3')?.message}
                            {...denseInputProps}
                          />
                        </Field>
                        <Field label="積込時間(分)" width={130}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={loadingUnloadingDuration.loadingDurationSeconds}
                            onChange={(event) => updateLoadingUnloadingDuration(index, 'loadingDurationSeconds', event.target.value)}
                            error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'loadingDurationSeconds')}
                            helperText={loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'loadingDurationSeconds')?.message}
                            {...denseInputProps}
                          />
                        </Field>
                        <Field label="降地時間(分)" width={130}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={loadingUnloadingDuration.unloadingDurationSeconds}
                            onChange={(event) => updateLoadingUnloadingDuration(index, 'unloadingDurationSeconds', event.target.value)}
                            error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'unloadingDurationSeconds')}
                            helperText={loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'unloadingDurationSeconds')?.message}
                            {...denseInputProps}
                          />
                        </Field>
                        <Stack direction="row" spacing={0.25} sx={{ mt: 2.25 }}>
                          <Tooltip title="この下に行を追加">
                            <IconButton
                              size="small"
                              aria-label="この下に行を追加"
                              onClick={() => addLoadingUnloadingDuration(index)}
                              disabled={hasRuleError}
                            >
                              <AddCircleOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="この行を削除">
                            <IconButton
                              size="small"
                              aria-label="この行を削除"
                              onClick={() => removeLoadingUnloadingDuration(index)}
                            >
                              <RemoveCircleOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </SectionCard>

              <SectionCard
                id="display"
                title="表示設定"
                description="配送リストや運行指示書の表示方法を設定します。"
                icon={SECTION_DEFS.display.icon}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>配送リストの地点表示</Typography>
                <FormControl fullWidth>
                  <RadioGroup
                    row
                    value={deliverylListGrouping ?? 'groupByAddress'}
                    onChange={onChangeDeliveryListGrouping}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel
                      value="groupByAddress"
                      control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: 12.5 }}>住所でまとめる</Typography>}
                    />
                    <FormControlLabel
                      value="groupByName"
                      control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: 12.5 }}>名称でまとめる</Typography>}
                    />
                  </RadioGroup>
                </FormControl>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>運行指示書に定型文を表示</Typography>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="運行指示書の下部に印字する定型文を入力"
                  id="operation_direction_memo"
                  {...register('operation_direction_memo')}
                  {...denseInputProps}
                />
              </SectionCard>

              <SectionCard
                id="fields"
                title="項目設定"
                description={`案件情報に任意の入力項目を最大${MAX_CUSTOM_INPUT_FIELDS}個設定できます。作成済みの項目は削除できません。名前が入力されていれば「使用中」、空欄なら「未使用」として扱われます。不要な項目は名前を空欄にして再利用できます。`}
                icon={SECTION_DEFS.fields.icon}
                action={(
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                    <Chip
                      size="small"
                      label={`使用中 ${filledCustomInputFields.length} / ${MAX_CUSTOM_INPUT_FIELDS}`}
                      sx={{ fontSize: 11.5, fontWeight: 700 }}
                    />
                    <Button
                      size="small"
                      startIcon={<AddCircleOutline />}
                      sx={{ fontSize: 12 }}
                      // 読み込み前に足しても、companyData 到着時の effect で上書きされて消える
                      disabled={!companyData || customInputFields.length >= MAX_CUSTOM_INPUT_FIELDS}
                      onClick={addCustomInputField}
                    >
                      項目を追加
                    </Button>
                  </Stack>
                )}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mb: 1 }}
                  data-testid="custom-input-field-filters"
                >
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mr: 0.5 }}>状態</Typography>
                  {CUSTOM_INPUT_FIELD_FILTERS.map(({ key, label }) => (
                    <Chip
                      key={key}
                      size="small"
                      label={label}
                      color={customInputFieldFilter === key ? 'primary' : 'default'}
                      variant={customInputFieldFilter === key ? 'filled' : 'outlined'}
                      onClick={() => setCustomInputFieldFilter(key)}
                      sx={{ fontSize: 11, fontWeight: 600 }}
                    />
                  ))}
                </Stack>
                <Box
                  data-testid="custom-input-field-slots"
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                  }}
                >
                  {visibleCustomInputFields.map(({ value, index }) => (
                    <Stack
                      key={['customInputField', index].join('-')}
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        label={`任意項目 ${index + 1}`}
                        placeholder="項目名を入力"
                        value={value}
                        onChange={(event) => updateCustomInputField(index, event.target.value)}
                        InputLabelProps={{ sx: { fontSize: 12 } }}
                        sx={value ? undefined : {
                          '& .MuiOutlinedInput-root, & .MuiInputLabel-root': { opacity: 0.6 },
                        }}
                        {...denseInputProps}
                      />
                      <Tooltip title={value ? '案件入力欄に表示されています' : '名前を入力すると使用中になります'}>
                        <Chip
                          size="small"
                          label={value ? '使用中' : '未使用'}
                          color={value ? 'success' : 'default'}
                          variant={value ? 'filled' : 'outlined'}
                          sx={{
                            fontSize: 10, fontWeight: 600, height: 24, flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    </Stack>
                  ))}
                </Box>
                {visibleCustomInputFields.length === 0 && (
                  <Typography sx={{
                    fontSize: 12, color: 'text.secondary', py: 2, textAlign: 'center',
                  }}
                  >
                    {/* 読み込み中に「ありません」と出すと設定が消えたように見えるので出し分ける */}
                    {companyData ? '該当する項目はありません。' : '読み込み中...'}
                  </Typography>
                )}
                {customInputFields.length >= MAX_CUSTOM_INPUT_FIELDS && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 1 }}>
                    {`上限（${MAX_CUSTOM_INPUT_FIELDS}件）に達しました。`}
                  </Typography>
                )}
              </SectionCard>

              {/*
                車両表示設定は会社設定とは別の API で即時保存するので、このフォームの
                dirty にも submit にも混ぜない。入力欄は Dialog（portal）の中だけだが、
                React ツリー上はこのフォームの子なので markDirtyFromForm で弾いている
              */}
              <VehicleAttributeSection />
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
});

export default EngineConfigPresenter;
